import React, { useEffect, useRef, useState } from 'react';
import { Platform, ScrollView, View } from 'react-native';
import { Button, Card, Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/src/components/Logo';
import type { MobileStorePlatform } from '@/src/utils/mobileWebPlatform';
import { openInAppUrl } from '@/src/config/appStores';
import { AppStoreLinks } from './AppStoreLinks';
import { checkReferralEligibility } from '@/src/components/referrals/referralEligibility';

interface Props {
  referralCode: string;
  platform: MobileStorePlatform | null;
  /** Preserves attribution unless the visitor explicitly declines it. */
  onContinueInBrowser: (withoutReferral?: boolean) => void;
}

/**
 * Shared desktop/mobile referral landing. Store installation never guarantees
 * code preservation; the readable code is available for manual native entry.
 * Attribution still happens only at signup, validated by the server (D-057).
 */
export function AppInstallLanding({ referralCode, platform, onContinueInBrowser }: Props) {
  const { t } = useTranslation('register');
  const theme = useTheme();
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [eligibility, setEligibility] = useState<'loading' | 'valid' | 'invalid' | 'unavailable'>('loading');
  const [attempt, setAttempt] = useState(0);
  const epoch = useRef(0);
  useEffect(() => {
    const current = ++epoch.current;
    setEligibility('loading'); setCopy('idle');
    void checkReferralEligibility(referralCode).then(result => {
      if (current === epoch.current) setEligibility(result);
    });
    return () => { epoch.current += 1; };
  }, [referralCode, attempt]);

  const copyCode = async () => {
    const current = epoch.current;
    try {
      await navigator.clipboard.writeText(referralCode);
      if (current === epoch.current) setCopy('copied');
    } catch {
      if (current === epoch.current) setCopy('failed');
    }
  };

  const openApp = () => {
    const url = openInAppUrl(referralCode);
    if (Platform.OS === 'web' && typeof window !== 'undefined') window.location.href = url;
  };

  return (
    <ScrollView
      testID="app-install-landing"
      style={{ backgroundColor: theme.colors.background }}
      contentContainerStyle={{ padding: 16, gap: 16, maxWidth: 520, width: '100%', alignSelf: 'center' }}
    >
      <View accessible accessibilityLabel="AllDrivers" style={{ alignItems: 'center' }}><Logo size="medium" /></View>
      <Text variant="headlineSmall" accessibilityRole="header">{t('appLandingTitle')}</Text>
      <Text variant="bodyLarge" style={{ color: theme.colors.onSurfaceVariant }}>
        {t('appLandingManualCode')}
      </Text>
      {eligibility === 'valid' && <Button mode="contained" contentStyle={{ minHeight: 44 }}
        testID="app-landing-continue-web" onPress={() => onContinueInBrowser(false)}>{t('appLandingRegisterNow')}</Button>}
      {eligibility === 'loading' && <Text accessibilityLiveRegion="polite">{t('appLandingChecking')}</Text>}
      {eligibility === 'invalid' && <Text testID="app-landing-invalid" accessibilityRole="alert">{t('appLandingInvalid')}</Text>}
      {eligibility === 'unavailable' && <View style={{ gap: 8 }}>
        <Text accessibilityRole="alert">{t('appLandingUnavailable')}</Text>
        <Button onPress={() => setAttempt(value => value + 1)}>{t('retry')}</Button>
      </View>}
      {(eligibility === 'invalid' || eligibility === 'unavailable') && <>
        <Text>{t('appLandingWithoutWarning')}</Text>
        <Button mode="contained" testID="app-landing-continue-without" contentStyle={{ minHeight: 44 }}
          onPress={() => onContinueInBrowser(true)}>{t('appLandingWithout')}</Button>
      </>}
      <AppStoreLinks
        testID="app-landing-store-links"
        referralCode={eligibility === 'valid' ? referralCode : null}
        primary={platform}
        title={t('appLandingStoresTitle')}
        message={t('appLandingStoresMessage')}
      />
      {eligibility === 'valid' && <><Card testID="app-landing-code" style={{ borderRadius: 16 }}>
        <Card.Content style={{ gap: 8, paddingVertical: 12 }}>
          <Text variant="labelLarge">{t('appLandingCodeLabel')}</Text>
          <Text testID="app-landing-code-value" selectable variant="headlineMedium">{referralCode}</Text>
          <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('appLandingCodeHelp')}</Text>
          <Button mode="outlined" icon="content-copy" contentStyle={{ minHeight: 44 }} testID="app-landing-copy" onPress={() => void copyCode()}>
            {t('appLandingCopy')}
          </Button>
          {copy === 'copied' && <Text testID="app-landing-copied" accessibilityLiveRegion="polite">{t('appLandingCopied')}</Text>}
          {copy === 'failed' && <Text testID="app-landing-copy-failed" accessibilityLiveRegion="polite" style={{ color: theme.colors.error }}>{t('appLandingCopyFailed')}</Text>}
        </Card.Content>
      </Card>
      <Button mode="contained-tonal" icon="open-in-app" contentStyle={{ minHeight: 44 }} testID="app-landing-open-app" onPress={openApp}>
        {t('appLandingOpenApp')}
      </Button>
      </>}
    </ScrollView>
  );
}

export default AppInstallLanding;
